import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, CircularProgress, Autocomplete } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import ContactListForm from '../../reuseable-components/ContactForm/ContactListForm';
import QuickbooksSearchSelect from '../../reuseable-components/QuickbooksSearchSelect/QuickbooksSearchSelect';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';

VendorForm.propTypes = {
  editVendor: PropTypes.object,
  setVendors: PropTypes.func,
  netTerms: PropTypes.array,
  contactTypes: PropTypes.array,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function VendorForm({ editVendor, setVendors, netTerms, contactTypes, open, close }) {
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [formValue, setFormValue] = useState({});

  useEffect(() => {
    if (editVendor) {
      setLoadingVendor(true);
      axios.get(`/vendor/vendor?id=${editVendor.id}`)
        .then(res => setFormValue(res.data ?? {}))
        .catch(() => enqueueSnackbar('Get vendor data failed', { variant: 'error' }))
        .finally(() => setLoadingVendor(false));
    } else setFormValue({ contacts: [{}] });
  }, [open])

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/vendor/vendorForm", objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(`Vendor ${(editVendor ? "Updated" : "Created")}`, { variant: 'success' });
          setVendors(vendors => updateItemInPlaceOrAppend(vendors, res.data));
          close();
        }
        else enqueueSnackbar(`${(editVendor ? "Update" : "Create")} vendor failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`${(editVendor ? "Update" : "Create")} vendor failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return <Dialog open={open} onClose={close} maxWidth="md" >
    <DialogContent>
      {!loadingVendor ? <Stack spacing={1} minWidth="600px">
        <Box>
          <Typography variant="subtitle1">Vendor Name*</Typography>
          <TextField
            name="name"
            size="small"
            placeholder="Enter Vendor Name"
            fullWidth
            value={formValue.name ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, name: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Vendor Account</Typography>
          <TextField
            name="accountNumber"
            size="small"
            placeholder="Enter Vendor Account"
            fullWidth
            value={formValue.accountNumber ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, accountNumber: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Street</Typography>
          <TextField
            name="streetAddress"
            size="small"
            placeholder="Enter Street Address"
            fullWidth
            value={formValue.streetAddress ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, streetAddress: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">City</Typography>
          <TextField
            name="city"
            size="small"
            placeholder="Enter City"
            fullWidth
            value={formValue.city ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, city: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">State</Typography>
          <TextField
            name="state"
            size="small"
            placeholder="Enter State"
            fullWidth
            value={formValue.state ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, state: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Zip</Typography>
          <TextField
            name="zip"
            size="small"
            placeholder="Enter Zip"
            fullWidth
            value={formValue.zip ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, zip: e.target.value }))}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">Phone Number</Typography>
          <TextField
            name="phoneNumber"
            size="small"
            placeholder="Enter Phone Number"
            fullWidth
            value={formValue.phoneNumber ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, phoneNumber: e.target.value }))}
          />
        </Box>
        <QuickbooksSearchSelect formValue={formValue} setFormValue={setFormValue} url="/quickbooks/vendorSearch" />    
        <Box>
          <Typography variant="subtitle1">Net Terms</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={netTerms.find(netTerm => netTerm.value === formValue.netTermId) ?? null}
            onChange={(event, val) => setFormValue(form => ({ ...form, netTermId: val?.value ?? 0 }))}
            options={netTerms}
            renderInput={(params) => <TextField {...params} placeholder="Net Terms" />}
          />
        </Box>
        <Box />
        <ContactListForm formValue={formValue} setFormValue={setFormValue} contactTypes={contactTypes} />
        <Box>
          <LoadingButton loading={submitting} disabled={!formValue?.name} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }}
            color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Box>
      </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </DialogContent>
  </Dialog>;
}
